import { Button, HStack, Input, Select, Stack, Text } from "@chakra-ui/react";
import { addDays, addHours, format } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { MdFileDownload } from "react-icons/md";
import {
    AccountRole,
    useAccountsQuery,
    useCalls2CsvMutation
} from "../../graphql";
import "react-datepicker/dist/react-datepicker.css";
import { getFullName } from "../../utils";
import debounce from "lodash.debounce";
import { useThrottle, useThrottleCallback } from "@react-hook/throttle";

export const AllCallsFilter = ({ onChange }) => {
    const now = new Date();
    const [generatingCSV, setGenerateCSV] = useState(false);
    const last30days = addDays(now, -30);
    const [doctorId, setDoctorId] = useState(null);
    const [specialistId, setSpecialistId] = useState(null);
    const [startDate, setStartDate] = useState(last30days);
    const [endDate, setEndDate] = useState(now);
    const [generateCSV] = useCalls2CsvMutation();

    const DateInput = ({ value, onClick }) => (
        <Button size="sm" onClick={ onClick }>
            { value }
        </Button>
    );

    const { data: doctorData, loading: loadingDocotrs } = useAccountsQuery({
        variables: {
            page: 1,
            pageLimit: 1000,
            role: AccountRole.Doctor,
            search: ""
        }
    });

    const {
        data: specialistData,
        loading: loadingSpecialists
    } = useAccountsQuery({
        variables: {
            page: 1,
            pageLimit: 1000,
            role: AccountRole.MedicalSpecialist,
            search: ""
        }
    });

    const onDownload = async (query) => {
        setGenerateCSV(true);

        const { data } = await generateCSV({
            variables: {
                page: 1,
                pageLimit: 20,
                where: {
                    accountId: query.accountId,
                    startDate: query.startDate
                        ? format(query.startDate, "yyyy-MM-dd")
                        : null,
                    endDate: query.endDate ? format(query.endDate, "yyyy-MM-dd") : null
                }
            }
        });

        const url = data.calls2csv.url;

        setGenerateCSV(false);

        let a = document.createElement("a");
        a.href = url;
        a.download = url.split("/").pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <HStack spacing={ 3 }>
            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Mjeku
                </Text>
                <Select
                    size="md"
                    placeholder="Zgjidh..."
                    disabled={ loadingDocotrs }
                    value={ doctorId }
                    onChange={ (event) => {
                        setSpecialistId("");
                        setDoctorId(parseInt(event.target.value));
                        onChange({
                            accountId: parseInt(event.target.value),
                            startDate,
                            endDate
                        });
                    } }
                >
                    { doctorData?.accounts?.nodes?.map((account) => (
                        <option value={ account.id }>{ getFullName(account) }</option>
                    )) }
                </Select>
            </Stack>

            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Specialisti
                </Text>
                <Select
                    size="md"
                    placeholder="Zgjidh..."
                    disabled={ loadingSpecialists }
                    value={ specialistId }
                    onChange={ (event) => {
                        setDoctorId("");
                        setSpecialistId(parseInt(event.target.value));
                        onChange({
                            accountId: parseInt(event.target.value),
                            startDate,
                            endDate
                        });
                    } }
                >
                    { specialistData?.accounts?.nodes?.map((account) => (
                        <option value={ account.id }>{ getFullName(account) }</option>
                    )) }
                </Select>
            </Stack>

            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Fillimi
                </Text>
                <DatePicker
                    selected={ startDate }
                    onChange={ (date) => {
                        if (date) {
                            setStartDate(date);
                            onChange({
                                accountId: doctorId || specialistId || null,
                                startDate: date,
                                endDate
                            });
                        }
                    } }
                    selectsStart
                    startDate={ startDate }
                    endDate={ endDate }
                    // @ts-ignore
                    customInput={ <DateInput /> }
                />
            </Stack>

            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Fundi
                </Text>
                <DatePicker
                    selected={ endDate }
                    onChange={ (date) => {
                        if (date) {
                            setEndDate(date);
                            onChange({
                                accountId: doctorId || specialistId || null,
                                startDate,
                                endDate: date
                            });
                        }
                    } }
                    selectsEnd
                    startDate={ startDate }
                    endDate={ endDate }
                    minDate={ startDate }
                    // @ts-ignore
                    customInput={ <DateInput /> }
                />
            </Stack>

            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Dokument CSV
                </Text>
                <Button
                    rightIcon={ <MdFileDownload /> }
                    colorScheme="blue"
                    size="sm"
                    variant="ghost"
                    width="100px"
                    isLoading={ generatingCSV }
                    onClick={ () => {
                        onDownload({
                            accountId: doctorId || specialistId || null,
                            startDate,
                            endDate
                        });
                    } }
                >
                    Shkarko
                </Button>
            </Stack>
        </HStack>
    );
};

export const DatesFilter = ({ onChange }) => {
    const { t } = useTranslation();
    const now = new Date();
    const last30days = addDays(now, -30);
    const [startDate, setStartDate] = useState(last30days);
    const [endDate, setEndDate] = useState(now);

    const DateInput = ({ value, onClick }) => (
        <Button size="sm" onClick={ onClick }>
            { value }
        </Button>
    );

    return (
        <HStack spacing={ 3 }>
            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    { t("calls.filterByDate") }
                </Text>
                <DatePicker
                    selected={ startDate }
                    onChange={ (date) => {
                        if (date) {
                            setStartDate(date);
                            onChange({
                                startDate: date,
                                endDate
                            });
                        }
                    } }
                    selectsStart
                    startDate={ startDate }
                    endDate={ endDate }
                    // @ts-ignore
                    customInput={ <DateInput /> }
                />
            </Stack>

            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }></Text>
                <DatePicker
                    selected={ endDate }
                    onChange={ (date) => {
                        if (date) {
                            setEndDate(date);
                            onChange({
                                startDate,
                                endDate: date
                            });
                        }
                    } }
                    selectsEnd
                    startDate={ startDate }
                    endDate={ endDate }
                    minDate={ startDate }
                    // @ts-ignore
                    customInput={ <DateInput /> }
                />
            </Stack>
        </HStack>
    );
};

export const useDebouncedEffect = (effect, delay, deps) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const callback = useCallback(effect, deps);
    const { t } = useTranslation();

    useEffect(() => {
        const handler = setTimeout(() => {
            callback();
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [callback, delay]);
};

export const MyContactListFilter = ({ onChange }) => {
    const { t } = useTranslation();
    const [term, setTerm] = useState("");
    const [date, setDate] = useState({ startDate: null, endDate: null });

    const handleChange = () => {
        onChange({
            term,
            startDate: date.startDate,
            endDate: date.endDate
        });
    };

    useDebouncedEffect(() => handleChange(), 1000, [term]);

    return (
        <HStack spacing={ 3 }>
            <Stack>
                <DatesFilter
                    onChange={ (data) => {
                        setDate(data);
                        onChange({
                            term,
                            ...data
                        });
                    } }
                />
            </Stack>
            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    { t("patients.patient") }
                </Text>
                <Input
                    placeholder={ t("patients.patient") }
                    size="md"
                    onChange={ (event) => setTerm(event.target.value) }
                />
            </Stack>
        </HStack>
    );
};
